/**
 * ============================================================================
 * useComputeScorecardMutation — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: PATCH /api/v1/vendor-evaluations/{id} — write the four axis
 *          scores back to the server, which recomputes the weighted score
 *          and classification (server is the source of truth on weights).
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type { VendorEvaluation } from './useEvaluationQuery';

export interface ComputeScorecardInput {
  evaluationId: string;
  quality: number;
  delivery: number;
  audit: number;
  service: number;
  rationale?: string;
}

export function useComputeScorecardMutation(): UseMutationResult<
  VendorEvaluation,
  Error,
  ComputeScorecardInput
> {
  return useMutation<VendorEvaluation, Error, ComputeScorecardInput>({
    mutationFn: ({ evaluationId, ...body }) =>
      apiFetch<VendorEvaluation>(
        `/api/v1/vendor-evaluations/${encodeURIComponent(evaluationId)}`,
        {
          method: 'PATCH',
          body: {
            axis_scores: [
              { axis: 'QUALITY', score: body.quality },
              { axis: 'DELIVERY', score: body.delivery },
              { axis: 'AUDIT', score: body.audit },
              { axis: 'SERVICE', score: body.service },
            ],
            rationale: body.rationale,
          },
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
